import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import { apiRequest } from "@mhj/shared/api";
import { useAuth } from "@mhj/shared/auth";

type Category = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  sortOrder: number;
  active: boolean | null;
};

type Track = {
  id: string;
  categoryId: string;
  name: string;
  slug: string;
  minAge: number | null;
  maxAge: number | null;
  genderRestriction: "any" | "female" | "male";
  awardTypes: string[];
  requiredDocs: string[];
  detailPrompt: string;
  sortOrder: number;
  active: boolean | null;
};

const AWARD_LABELS: Record<string, string> = {
  training: "Training",
  cash: "Cash",
  equipment: "Equipment",
};

/** Plain-language summary of who a track is open to. */
function eligibilitySummary(track: Track) {
  const parts: string[] = [];
  if (track.minAge != null && track.maxAge != null) {
    parts.push(`aged ${track.minAge}–${track.maxAge}`);
  } else if (track.minAge != null) {
    parts.push(`aged ${track.minAge} and over`);
  } else if (track.maxAge != null) {
    parts.push(`aged up to ${track.maxAge}`);
  }
  if (track.genderRestriction === "female") parts.push("women only");
  if (track.genderRestriction === "male") parts.push("men only");
  return parts.length ? parts.join(" · ") : "open to all applicants";
}

export default function AdminEmpowermentTracks() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isSuperAdmin = user?.role === "super_admin";
  const [editing, setEditing] = useState<Track | null>(null);

  const { data: categoryData, error: categoryError } = useQuery({
    queryKey: ["empowerment", "categories"],
    queryFn: () => apiRequest<{ categories: Category[] }>("/admin/empowerment/categories"),
  });

  const { data: trackData, error: trackError, isLoading } = useQuery({
    queryKey: ["empowerment", "tracks"],
    queryFn: () => apiRequest<{ tracks: Track[] }>("/admin/empowerment/tracks"),
  });

  useEffect(() => {
    const err = categoryError ?? trackError;
    if (err) toast.error((err as Error).message);
  }, [categoryError, trackError]);

  const saveTrack = useMutation({
    mutationFn: (track: Track) =>
      apiRequest(`/admin/empowerment/tracks/${track.id}`, {
        method: "PUT",
        withCsrf: true,
        data: {
          categoryId: track.categoryId,
          name: track.name,
          slug: track.slug,
          minAge: track.minAge,
          maxAge: track.maxAge,
          genderRestriction: track.genderRestriction,
          awardTypes: track.awardTypes,
          requiredDocs: track.requiredDocs,
          detailPrompt: track.detailPrompt,
          active: track.active ?? true,
          sortOrder: track.sortOrder,
        },
      }),
    onSuccess: () => {
      toast.success("Track saved");
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ["empowerment", "tracks"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const categories = categoryData?.categories ?? [];
  const tracks = trackData?.tracks ?? [];

  const grouped = useMemo(
    () =>
      categories.map((category) => ({
        category,
        tracks: tracks.filter((t) => t.categoryId === category.id),
      })),
    [categories, tracks],
  );

  return (
    <div className="space-y-6">
      <div className="glass-card rounded-3xl p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-blue-500">Empowerment</p>
        <h2 className="mt-2 text-2xl font-semibold text-slate-900">
          Categories &amp; tracks
        </h2>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          A category is a heading; a track is the specific trade an applicant
          picks. Each track carries its own age range, any women-only
          restriction, the documents it needs, and the one question asked of
          applicants who choose it.
        </p>
        {!isSuperAdmin && (
          <p className="mt-4 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
            You can view tracks. Editing them is restricted to a super admin.
          </p>
        )}
      </div>

      {isLoading ? (
        <div className="glass-card rounded-3xl p-6">
          <p className="text-sm text-slate-500">Loading tracks…</p>
        </div>
      ) : (
        grouped.map(({ category, tracks: categoryTracks }) => (
          <div key={category.id} className="glass-card rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="text-2xl" aria-hidden="true">{category.icon}</span>
              <div>
                <h3 className="font-semibold text-slate-900">{category.name}</h3>
                {category.description && (
                  <p className="text-xs text-slate-500">{category.description}</p>
                )}
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {categoryTracks.map((track) => (
                <div
                  key={track.id}
                  className={`rounded-2xl border p-4 ${
                    track.active ? "border-slate-200" : "border-slate-100 bg-slate-50 opacity-70"
                  }`}
                >
                  {editing?.id === track.id ? (
                    <form
                      className="space-y-3"
                      onSubmit={(e) => {
                        e.preventDefault();
                        saveTrack.mutate(editing);
                      }}
                    >
                      <div className="grid gap-3 sm:grid-cols-2">
                        <label className="flex flex-col gap-1 text-sm">
                          <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Name</span>
                          <input
                            value={editing.name}
                            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                            className="rounded-xl border border-sky-200 px-3 py-2"
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                          <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Who may apply</span>
                          <select
                            value={editing.genderRestriction}
                            onChange={(e) =>
                              setEditing({
                                ...editing,
                                genderRestriction: e.target.value as Track["genderRestriction"],
                              })
                            }
                            className="rounded-xl border border-sky-200 px-3 py-2"
                          >
                            <option value="any">Anyone</option>
                            <option value="female">Women only</option>
                            <option value="male">Men only</option>
                          </select>
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                          <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Minimum age</span>
                          <input
                            type="number"
                            value={editing.minAge ?? ""}
                            onChange={(e) =>
                              setEditing({
                                ...editing,
                                minAge: e.target.value === "" ? null : Number(e.target.value),
                              })
                            }
                            placeholder="no minimum"
                            className="rounded-xl border border-sky-200 px-3 py-2"
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                          <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Maximum age</span>
                          <input
                            type="number"
                            value={editing.maxAge ?? ""}
                            onChange={(e) =>
                              setEditing({
                                ...editing,
                                maxAge: e.target.value === "" ? null : Number(e.target.value),
                              })
                            }
                            placeholder="no maximum"
                            className="rounded-xl border border-sky-200 px-3 py-2"
                          />
                        </label>
                      </div>

                      <label className="flex flex-col gap-1 text-sm">
                        <span className="text-xs uppercase tracking-[0.2em] text-slate-500">
                          Question asked of applicants
                        </span>
                        <input
                          value={editing.detailPrompt}
                          onChange={(e) => setEditing({ ...editing, detailPrompt: e.target.value })}
                          className="rounded-xl border border-sky-200 px-3 py-2"
                        />
                      </label>

                      <div>
                        <span className="text-xs uppercase tracking-[0.2em] text-slate-500">
                          What this track can award
                        </span>
                        <div className="mt-1 flex gap-2">
                          {Object.entries(AWARD_LABELS).map(([value, label]) => {
                            const on = editing.awardTypes.includes(value);
                            return (
                              <button
                                key={value}
                                type="button"
                                onClick={() =>
                                  setEditing({
                                    ...editing,
                                    awardTypes: on
                                      ? editing.awardTypes.filter((a) => a !== value)
                                      : [...editing.awardTypes, value],
                                  })
                                }
                                className={`rounded-full border px-3 py-1.5 text-xs transition ${
                                  on
                                    ? "border-blue-500 bg-blue-50 text-blue-700"
                                    : "border-slate-200 text-slate-600"
                                }`}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                        <p className="mt-1 text-xs text-slate-400">
                          Including Cash makes bank details required on the application.
                        </p>
                      </div>

                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={editing.active ?? true}
                          onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                        />
                        Open to applicants
                      </label>

                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={saveTrack.isPending}
                          className="rounded-full bg-blue-600 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-blue-500 disabled:opacity-60"
                        >
                          {saveTrack.isPending ? "Saving…" : "Save"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditing(null)}
                          className="rounded-full border border-slate-200 px-5 py-2 text-xs uppercase tracking-[0.2em] text-slate-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-slate-900">
                          {track.name}
                          {!track.active && (
                            <span className="ml-2 text-xs font-normal text-slate-400">
                              (closed)
                            </span>
                          )}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {eligibilitySummary(track)}
                        </p>
                        <p className="mt-1 text-xs text-slate-400">
                          Awards: {track.awardTypes.map((a) => AWARD_LABELS[a] ?? a).join(", ")}
                          {" · "}
                          {track.requiredDocs.length} documents
                        </p>
                      </div>
                      {isSuperAdmin && (
                        <button
                          type="button"
                          onClick={() => setEditing(track)}
                          className="rounded-full border border-blue-200 px-4 py-1.5 text-xs uppercase tracking-wide text-blue-700 transition hover:bg-blue-50"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
